import type { SxProps, Theme } from "@mui/material";
import { TextField } from "@mui/material";
import type { CellContext, RowData } from "@tanstack/react-table";
import { memo, useCallback } from "react";

import BaseCell from "./BaseCell";
import type { TColumn, TTable } from "./types";

interface Props<TData extends RowData, TValue> extends CellContext<TData, TValue> {
    value: string | undefined;
    updateData?: (o: { rowId: string | number; rowIndex: number; columnId: string; value: string | number }) => void;
    onBlur?: (rowIndex: number, columnId: string, value: string) => void;
    sx?: SxProps<Theme>;
    className?: string;
    helperText?: string;
    localCellValues?: Record<string, Record<string, string>>;
}

function EditableCell<C extends TColumn[], TValue>({
    row,
    column: { id: columnId },
    value,
    updateData,
    onBlur,
    sx,
    className,
    helperText,
    localCellValues,
}: Props<TTable<C>, TValue>) {
    const rowId = row.original.id;
    const rowIndex = row.index;

    // Use local value if available, otherwise use the passed value
    const currentValue = localCellValues?.[rowId]?.[columnId] ?? value;

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            console.log(`EditableCell handleChange - rowId: ${rowId}, columnId: ${columnId}, newValue: ${newValue}`);

            if (updateData) {
                updateData({ rowId, rowIndex, columnId, value: newValue });
            }
        },
        [rowId, rowIndex, columnId, updateData],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            if (onBlur) {
                onBlur(rowIndex, columnId, newValue);
            }
        },
        [rowIndex, columnId, onBlur],
    );

    return (
        <BaseCell sx={sx} className={className} helperText={helperText}>
            <TextField
                key={`${rowId}-${columnId}`}
                variant="filled"
                value={currentValue}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                sx={{
                    height: "100%",
                    padding: "0px",
                    ".MuiFilledInput-root": {
                        height: "100%",
                        padding: "0px",
                        borderRadius: "0px",
                    },
                    ".MuiFilledInput-input": {
                        textAlign: "left",
                        height: "100%",
                        padding: "0px 4px",
                        fontSize: "13px",
                        fontWeight: 600,
                    },
                }}
                InputProps={{ disableUnderline: true }}
            />
        </BaseCell>
    );
}

export default memo(EditableCell, (prevProps, nextProps) => {
    const prevRowId = prevProps.row.original.id;
    const nextRowId = nextProps.row.original.id;
    const columnId = prevProps.column.id;

    // Compare the actual values that matter for this cell
    const prevValue = prevProps.localCellValues?.[prevRowId]?.[columnId] ?? prevProps.value;
    const nextValue = nextProps.localCellValues?.[nextRowId]?.[columnId] ?? nextProps.value;

    // Only re-render if the essential props have changed
    return (
        prevValue === nextValue &&
        prevRowId === nextRowId &&
        prevProps.row.index === nextProps.row.index &&
        prevProps.column.id === nextProps.column.id &&
        prevProps.updateData === nextProps.updateData &&
        prevProps.onBlur === nextProps.onBlur &&
        prevProps.className === nextProps.className &&
        prevProps.helperText === nextProps.helperText &&
        // Deep compare sx prop
        JSON.stringify(prevProps.sx) === JSON.stringify(nextProps.sx)
    );
});
